import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft,
  ArrowRight,
  Edit, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  Calendar,
  FileText,
  Eye,
  User,
  Clock,
  Users
} from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminTender, useUpdateTender, useDeleteTender, useDeleteTenderDocument } from "@/hooks/api/use-admin";
import { adminService } from "@/api/services/admin.service";
import { cn } from "@/lib/utils";

const AdminTenderDetails = () => {
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isAdmin = user?.type === 'admin';
  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;
  const [activeTab, setActiveTab] = useState("info");

  const { data: tenderDetails, isLoading, error } = useAdminTender(
    parseInt(id || '0'),
    { enabled: isAdmin && !!id }
  );

  const updateMutation = useUpdateTender();
  const deleteMutation = useDeleteTender();
  const deleteDocumentMutation = useDeleteTenderDocument();

  // Helper functions
  const getTranslatedText = (field: string | { en: string; ar: string }) => {
    if (typeof field === 'string') return field;
    return field[language] || field.en || '';
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: {
        label: t('common.active') || 'نشط',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        icon: CheckCircle,
      },
      pending: {
        label: t('common.pending') || 'قيد المراجعة',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        icon: Clock,
      },
      closed: {
        label: t('common.closed') || 'مغلق',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        icon: XCircle,
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={cn(config.className, "flex items-center gap-1")}>
        <Icon size={12} />
        {config.label}
      </Badge>
    );
  };

  const handleStatusChange = (newStatus: 'active' | 'closed' | 'pending') => {
    if (!tenderDetails?.data) return;
    
    updateMutation.mutate({
      id: tenderDetails.data.id,
      data: { status: newStatus }
    });
  };

  const handleDelete = () => {
    if (!tenderDetails?.data) return;
    
    if (confirm(t('tenders.confirmDelete') || 'هل أنت متأكد من حذف هذه المناقصة؟')) {
      deleteMutation.mutate(tenderDetails.data.id, {
        onSuccess: () => {
          navigate('/admin/dashboard/tenders');
        }
      });
    }
  };

  const handleEdit = () => {
    if (!tenderDetails?.data) return;
    navigate(`/admin/dashboard/tenders/${tenderDetails.data.id}/edit`);
  };

  const handleDownloadDocument = async (doc: any) => {
    if (!tenderDetails?.data) return;
    
    try {
      await adminService.downloadTenderDocument(
        tenderDetails.data.id,
        doc.id,
        doc.original_name || doc.filename || doc.file_name || doc.name
      );
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to window.open method if API method fails
      const downloadUrl = `/api/v1/admin/tenders/${tenderDetails.data.id}/documents/${doc.id}/download`;
      window.open(downloadUrl, '_blank');
    }
  };

  const handleDeleteDocument = (doc: any) => {
    if (!tenderDetails?.data) return;
    
    if (confirm(t('tenders.confirmDeleteDocument') || 'هل أنت متأكد من حذف هذا المستند؟')) {
      deleteDocumentMutation.mutate({
        tenderId: tenderDetails.data.id,
        documentId: doc.id
      });
    }
  };

  // Only admin can access
  if (!isAdmin) {
    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center py-8 sm:py-12">
          <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center mb-3">
            <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 dark:text-red-400" />
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('admin.tenders.adminOnly') || 'هذه الصفحة متاحة فقط للمسؤولين'}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <Skeleton className="h-9 w-9 sm:h-10 sm:w-10" />
          <Skeleton className="h-6 sm:h-8 w-32 sm:w-48" />
        </div>
        <div className="space-y-3 sm:space-y-4">
          <Skeleton className="h-24 sm:h-32 w-full" />
          <Skeleton className="h-24 sm:h-32 w-full" />
          <Skeleton className="h-24 sm:h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error || !tenderDetails?.data) {
    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center py-8 sm:py-12">
          <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
            <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('admin.tenders.notFound') || 'المناقصة غير موجودة'}
          </p>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/dashboard/tenders')}
            className="mt-4 text-xs sm:text-sm"
          >
            <ArrowIcon className={`${isRTL ? 'ml-2' : 'mr-2'} h-3.5 w-3.5 sm:h-4 sm:w-4`} />
            {t('common.back') || 'العودة'}
          </Button>
        </div>
      </div>
    );
  }

  const tender = tenderDetails.data;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col gap-4">
            {/* Top row: Back button */}
            <div className="flex items-center justify-between">
              <Link to="/admin/dashboard/tenders" className="group">
                <Button variant="outline" size="sm" className="group-hover:bg-gray-50 dark:group-hover:bg-gray-700 transition-colors text-xs sm:text-sm">
                  <ArrowIcon className={`${isRTL ? 'ml-2' : 'mr-2'} h-3.5 w-3.5 sm:h-4 sm:w-4`} />
                  {t('common.back') || 'Back'}
                </Button>
              </Link>
              
              {/* Action Buttons - Desktop */}
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  className="text-xs sm:text-sm"
                >
                  <Edit size={14} className={`${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                  {t('admin.tenders.edit') || 'Edit'}
                </Button>
                {tender.status === 'pending' && (
                  <Button
                    size="sm"
                    onClick={() => handleStatusChange('active')}
                    className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm"
                  >
                    <CheckCircle size={14} className={`${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                    {t('admin.tenders.activate') || 'Activate'}
                  </Button>
                )}
                {tender.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange('closed')}
                    className="text-xs sm:text-sm"
                  >
                    <XCircle size={14} className={`${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                    {t('admin.tenders.close') || 'Close'}
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="text-xs sm:text-sm"
                >
                  <Trash2 size={14} className={`${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                  {t('admin.tenders.delete') || 'Delete'}
                </Button>
              </div>
            </div>
            
            {/* Title row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 line-clamp-2">
                  {getTranslatedText(tender.title)}
                </h1>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${
                    tender.status === 'active' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' 
                      : tender.status === 'closed'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                  }`}>
                    {getStatusBadge(tender.status)}
                  </Badge>
                  <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    #{tender.id}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons - Mobile */}
              <div className="flex md:hidden items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  className="flex-1 text-xs h-8"
                >
                  <Edit size={14} className={`${isRTL ? 'ml-1' : 'mr-1'}`} />
                  {t('admin.tenders.edit') || 'Edit'}
                </Button>
                {tender.status === 'pending' && (
                  <Button
                    size="sm"
                    onClick={() => handleStatusChange('active')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                  >
                    <CheckCircle size={14} className={`${isRTL ? 'ml-1' : 'mr-1'}`} />
                    {t('admin.tenders.activate') || 'Activate'}
                  </Button>
                )}
                {tender.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange('closed')}
                    className="flex-1 text-xs h-8"
                  >
                    <XCircle size={14} className={`${isRTL ? 'ml-1' : 'mr-1'}`} />
                    {t('admin.tenders.close') || 'Close'}
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="text-xs h-8"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
            <TabsTrigger value="info" className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
              <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('admin.tenders.basicInfo') || 'Basic Information'}</span>
              <span className="sm:hidden">{isRTL ? 'المعلومات' : 'Info'}</span>
            </TabsTrigger>
            <TabsTrigger value="offers" className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('admin.tenders.offers') || 'Offers'} ({(tender as any).offers?.length || 0})</span>
              <span className="sm:hidden">{isRTL ? 'العروض' : 'Offers'} ({(tender as any).offers?.length || 0})</span>
            </TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="info" className="space-y-4 sm:space-y-6">
            <Tabs defaultValue="details" className="space-y-4 sm:space-y-6">
              <TabsList className="grid w-full grid-cols-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
                <TabsTrigger value="details" className={`flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="truncate">{isRTL ? 'التفاصيل' : 'Details'}</span>
                </TabsTrigger>
                <TabsTrigger value="dates" className={`flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="truncate">{isRTL ? 'التواريخ' : 'Dates'}</span>
                </TabsTrigger>
                <TabsTrigger value="documents" className={`flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs data-[state=active]:bg-primary data-[state=active]:text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="truncate">{isRTL ? 'الملفات' : 'Documents'}</span>
                </TabsTrigger>
              </TabsList>

              {/* Details Sub-tab */}
              <TabsContent value="details" className="space-y-3 sm:space-y-4">
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <FileText className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 sm:h-5 sm:w-5`} />
                      {t('admin.tenders.details') || 'Details'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    <div>
                      <h4 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {t('admin.tenders.title') || 'Title'}
                      </h4>
                      <p className="text-sm sm:text-base text-gray-900 dark:text-gray-100">
                        {getTranslatedText(tender.title)}
                      </p>
                    </div>
                    {tender.description && (
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {t('admin.tenders.description') || 'Description'}
                        </h4>
                        <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {getTranslatedText(tender.description)}
                        </p>
                      </div>
                    )}
                    {tender.requirements && (
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                          {t('admin.tenders.requirements') || 'Requirements'}
                        </h4>
                        <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {getTranslatedText(tender.requirements)}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Status & Dates Sub-tab */}
              <TabsContent value="dates" className="space-y-3 sm:space-y-4">
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <Calendar className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 sm:h-5 sm:w-5`} />
                      {t('admin.tenders.statusAndDates') || 'Status & Dates'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <label className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                          {t('common.status') || 'Status'}
                        </label>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(tender.status)}
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <label className="text-xs sm:text-sm font-medium text-amber-700 dark:text-amber-400">
                          {t('admin.tenders.deadline') || 'Deadline'}
                        </label>
                        <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-300 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {(() => {
                            try {
                              const date = new Date(tender.deadline);
                              return format(date, "PPP", { locale: isRTL ? ar : enUS });
                            } catch {
                              return tender.deadline;
                            }
                          })()}
                        </p>
                      </div>
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <label className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                          {t('admin.tenders.createdAt') || 'Created At'}
                        </label>
                        <p className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {(() => {
                            try {
                              const date = new Date(tender.created_at);
                              return format(date, "PPP", { locale: isRTL ? ar : enUS });
                            } catch {
                              return tender.created_at;
                            }
                          })()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documents Sub-tab */}
              <TabsContent value="documents" className="space-y-3 sm:space-y-4">
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <FileText className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 sm:h-5 sm:w-5`} />
                      {t('admin.tenders.documents') || 'Documents'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6">
                    {tender.documents && tender.documents.length > 0 ? (
                      <div className="space-y-2 sm:space-y-3">
                        {tender.documents.map((doc: any) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex-shrink-0">
                                <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {doc.original_name || doc.file_name}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                                    {doc.formatted_size || `${Math.round(doc.file_size / 1024)} KB`}
                                  </p>
                                  {doc.document_type && (
                                    <Badge variant="outline" className="text-[10px] sm:text-xs">
                                      {doc.document_type}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadDocument(doc)}
                                className="text-xs h-8"
                              >
                                {t('common.download') || 'Download'}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteDocument(doc)}
                                className="text-xs h-8"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 sm:py-12">
                        <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                          <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                          {t('admin.tenders.noDocuments') || 'No documents uploaded'}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers" className="space-y-4 sm:space-y-6">
            <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="flex items-center text-base sm:text-lg">
                  <Users className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 sm:h-5 sm:w-5`} />
                  {t('admin.tenders.offers') || 'Offers'} ({(tender as any).offers?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {(tender as any).offers && (tender as any).offers.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                    {(tender as any).offers.map((offer: any) => (
                      <div key={offer.id} className="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="p-1.5 rounded-lg bg-primary/10">
                                <User className="h-3.5 w-3.5 text-primary" />
                              </div>
                              <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-gray-100 truncate">
                                {offer.client?.name || offer.client?.company_name || `Client ${offer.client_id}`}
                              </span>
                              <Badge variant="outline" className="text-[10px] sm:text-xs">
                                #{offer.id}
                              </Badge>
                            </div>
                            {offer.description && (
                              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                                {offer.description}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 mt-2">
                              {offer.price && (
                                <p className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
                                  {isRTL ? 'ر.س ' : ''}{offer.price.toLocaleString()}{isRTL ? '' : ' SAR'}
                                </p>
                              )}
                              {offer.created_at && (
                                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {(() => {
                                    try {
                                      const date = new Date(offer.created_at);
                                      return format(date, "PP", { locale: isRTL ? ar : enUS });
                                    } catch {
                                      return offer.created_at;
                                    }
                                  })()}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/dashboard/offers/${offer.id}`)}
                            className="w-full sm:w-auto text-xs h-8"
                          >
                            <Eye size={14} className={`${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                            {t('common.view') || 'View'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 sm:py-12">
                    <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                      <Users className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {t('admin.tenders.noOffers') || 'No offers yet'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminTenderDetails;
